import React, { useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import Alert from '@material-ui/lab/Alert';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { createFFmpeg, fetchFile } from '@ffmpeg/ffmpeg';

const SHOW_DEBUG = false;

let ffmpeg:any = null;

const useStyles = makeStyles({
  root: {
    marginTop: '48px',
    marginBottom: '48px'
  },
  progress: {
    width: '100%',
  },
  createAgainCTA: {
    margin:"50px 0 25px"
  },
  button: {
    fontWeight:"bold"
  },
  finePrint: {
    opacity:0.75,
    marginTop:10,
    fontSize:"14px"
  }
});

const readFromBlobOrFile = (blob: File): Promise<ArrayBuffer> => (
  new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      resolve(fileReader.result as ArrayBuffer);
    };
    fileReader.onerror = ({ target: { error: { code } } }: any) => {
      reject(Error(`File could not be read! Code=${code}`));
    };
    fileReader.readAsArrayBuffer(blob);
  })
);

type CircularProgressWithLabelProps = {
  value: number,
  variant: string,
  color: string
}
function CircularProgressWithLabel(props: any) {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgress variant="static" {...props} />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="caption" component="div" color="textSecondary">{`${Math.round(
          props.value,
        )}%`}</Typography>
      </Box>
    </Box>
  );
}
type FFmpegProps = {
  args: Array<string>;
  inFilename: string;
  outFilename: string;
  mediaType: string;
  inputTypeHuman: string;
}

function FFmpeg({ args, inFilename, outFilename, mediaType, inputTypeHuman }: FFmpegProps) {
  const classes = useStyles();
  const [videoSrc, setVideoSrc] = useState('');
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [debugMessage, setDebugMessage] = useState('');
  const [downloadFileName, setDownloadFileName] = useState('');
  const [jobRunning, setJobRunning] = useState(false);

  useEffect(() => {
    if (ffmpeg === null) {
      let target = 'mobile';
      
      ffmpeg = createFFmpeg({
        // log: true,
        corePath: `./static/js/ffmpeg/${target}/ffmpeg-core.js`,
        // corePath: 'https://unpkg.com/@ffmpeg/core@0.8.3/dist/ffmpeg-core.js',
      });
      ffmpeg.load()
    }
    ffmpeg.setLogger(({ type, message }: {type:string, message:string}) => {
      if (type !== 'info') { setDebugMessage(message); }  
    });
    ffmpeg.setProgress(({ ratio }: {ratio: number}) => {
      
      if (ratio >= 0 && ratio <= 1) {
        setProgress(ratio * 100);
      }
      if (ratio === 1) {
        setTimeout(() => { 
          
          setProgress(0); 
        }, 1000);
      }
    });
  });

  const getAudioFileName = (name: string) => {
    let parts = name.split('.'),
        fileSuffix = "-getaudiogram.com",
        fileExtension = "mp3";

    parts[parts.length-2] += fileSuffix;
    parts[parts.length-1] = fileExtension; 
    return parts.join('.');
  }

  const onFileUploaded = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target;
    const files = target.files;
    if( files && files.length > 0 ){
      
      setError('')
      setVideoSrc('')
      setProgress(0)
      setJobRunning(true)

      const fileInput: File = files[0];
      const name = fileInput.name;
      setDownloadFileName(getAudioFileName(name))

      const file = new Uint8Array(await readFromBlobOrFile(fileInput));
      
      if (!ffmpeg.isLoaded()) {
        setMessage('Loading media conversion tool, this may take a few minutes. Your job will start automatically.');
        await ffmpeg.load();
        setMessage('');
      }
      
      ffmpeg.FS('writeFile', name, await fetchFile(file as any));
      
      const start = Date.now();
      await ffmpeg.run(...args.map((a:string) => a === "<INPUT>" ? name : a));
      
      try {
        const data = ffmpeg.FS('readFile', outFilename);
        setVideoSrc(URL.createObjectURL(new Blob([data.buffer], { type: mediaType })));
        setJobRunning(false)
      }catch(e){
        console.error(e);
        setError('There was a problem converting your file.')
        setJobRunning(false)
      }
      
      
    }
  };

  return (
    <Grid className={classes.root} container direction="column" alignItems="center" spacing={2}>
      {videoSrc.length === 0 ? null : (
        <Grid container direction="row" justifyContent="center" alignItems="center" spacing={2}>
          <Grid container item xs={10} md={4} justifyContent="center" alignItems="center">
            <audio src={videoSrc} autoPlay controls></audio>
          </Grid>
          <Grid container item xs={10} md={3} justifyContent="center" alignItems="center">
            <Link
              href={videoSrc}
              download={downloadFileName}
            >
              <Button
                className={classes.button}
                variant="contained"
                component="label"
                color="primary"
              >
                Download Audio
              </Button>
            </Link>
          </Grid>
        </Grid>
      )}
      
      {jobRunning && !message && (
        <Grid item container direction="row" justifyContent="space-around" alignItems="center" md={4} xs={12}>
          <CircularProgressWithLabel
            variant="determinate"
            color="primary"
            value={progress}
          />
          <Typography variant="body1">Extracting audio...</Typography>
        </Grid>
      )}
      { message && ( <Alert severity="info">{message}</Alert> ) }
      { !jobRunning && 
        (<Grid container item xs={10} md={6} justifyContent="center" direction="column">
          {videoSrc.length !== 0 && (
            <Typography className={classes.createAgainCTA} variant="h6" align="center">Want to convert another file?</Typography>
          )}
          <Button
            className={classes.button}
            variant="contained"
            component="label"
            color="primary"
          >
            Select a {inputTypeHuman} File
            <input
              type="file"
              accept="video/*"
              style={{ display: 'none' }}
              onChange={onFileUploaded}
            />
          </Button>
          <Typography className={classes.finePrint} align="center" variant="body1">
            Supported file types: .mp4, .m4v, .avi, .mpg, .mpeg, .mov
          </Typography>  
          <br/>
          <br/>
          <Typography align="center" variant="body1">
            Completely private, we don’t upload or store your video file
          </Typography>  
          
        </Grid>)
      }
      { error && ( <Alert severity="error">{error}</Alert> ) }
      
      
      
    </Grid>
  );
}

export default FFmpeg;
